import React from 'react';
import * as Tooltip from '@radix-ui/react-tooltip';
import { InfoCircledIcon } from '@radix-ui/react-icons';
import styles from './InfoTooltip.module.css';

/**
 * Small `i`-in-a-circle button that, on hover or keyboard
 * focus, reveals a short explanatory tooltip. Use it next to
 * column headers / labels / summary card titles where the
 * meaning isn't self-evident — never as a substitute for
 * fixing an ambiguous label.
 *
 * Props
 *   text  — the tooltip body (one short sentence recommended)
 *   side  — 'top' (default) | 'right' | 'bottom' | 'left'
 *   align — 'start' | 'center' (default) | 'end'
 *
 * Implementation notes
 *   - Uses Radix Tooltip primitives — accessible by default
 *     (keyboard focus opens the tooltip; Esc dismisses).
 *   - Provider lives inside the component so callers don't
 *     have to mount one at the app root. Multiple Providers
 *     coexist safely; the runtime cost is negligible.
 *   - Portal is used so the tooltip floats above tables,
 *     modals, and other clipping ancestors.
 */
const InfoTooltip = ({ text, side = 'top', align = 'center' }) => {
  if (!text) return null;
  return (
    <Tooltip.Provider delayDuration={250} skipDelayDuration={150}>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <button
            type="button"
            className={styles.trigger}
            aria-label="More information"
          >
            <InfoCircledIcon className={styles.icon} aria-hidden="true" />
          </button>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            className={styles.content}
            side={side}
            align={align}
            sideOffset={6}
            collisionPadding={8}
          >
            {text}
            <Tooltip.Arrow className={styles.arrow} />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
};

export default InfoTooltip;
